'use client'

import { Badge } from '@/components/v2/ui/badge'
import { Button } from '@/components/v2/ui/button'
import Icon from '@/components/v2/ui/icon'
import { useTexts } from '@/contexts/LanguageContext'
import { cn } from '@/lib/utils'
import RequestAccessDialog from '../../components/RequestAccessDialog/RequestAccessDialog'
import type { IDocument } from '../useDocuments'

interface IDocumentRowProps {
  document: IDocument
  privateNames: string[]
}

function DocumentAction({ document, privateNames }: IDocumentRowProps) {
  const { RESOURCES } = useTexts().PUBLIC_TRUST_CENTER
  const { resource, isPublic } = document
  if (isPublic && resource.url) {
    return (
      <Button variant="outline" size="sm" asChild className="shadow-none">
        <a href={resource.url} target="_blank" rel="noopener noreferrer" data-testid="view-public-resource">
          <Icon icon="download" className="size-3.5" />
          {RESOURCES.DOWNLOAD}
        </a>
      </Button>
    )
  }
  return (
    <RequestAccessDialog
      options={privateNames.includes(resource.name) ? privateNames : [resource.name, ...privateNames]}
      defaultSelected={[resource.name]}
    >
      <Button variant="outline" size="sm" className="shadow-none">
        <Icon icon="lock" className="size-3.5" />
        {RESOURCES.REQUEST}
      </Button>
    </RequestAccessDialog>
  )
}

export default function DocumentRow({ document, privateNames }: IDocumentRowProps) {
  const { RESOURCES } = useTexts().PUBLIC_TRUST_CENTER
  const { resource, isPublic } = document
  return (
    <div className="flex items-center gap-3.5 border-t border-trust-center-border px-4 py-3 first:border-t-0">
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded border border-trust-center-border',
          isPublic ? 'text-trust-center-muted' : 'text-trust-center-accent',
        )}
      >
        <Icon icon={isPublic ? 'file-text' : 'lock'} className="size-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{resource.name}</span>
        <span className="truncate text-xs text-trust-center-muted">{resource.description}</span>
      </span>
      <Badge
        variant="outline"
        className={cn(
          'rounded-full',
          isPublic
            ? 'border-trust-center-border bg-trust-center-subtle text-trust-center-muted'
            : 'border-trust-center-border-strong text-trust-center-accent',
        )}
      >
        {isPublic ? RESOURCES.PUBLIC : RESOURCES.PRIVATE}
      </Badge>
      <DocumentAction document={document} privateNames={privateNames} />
    </div>
  )
}
